import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import Sidedash from '../uicomponents/Sidedash';
import debounce from 'lodash.debounce';
import LoadingContext from '../../context/LoadingContext'
import AlertContext from '../../context/AlertContext';
import { useSettings } from '../../context/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/Utilsfunc';
import { sumSeatLinePrices } from '../../utils/slotPricing';

const BookingForm = () => {

    const host = process.env.REACT_APP_BACKEND_URL;
    const navigate = useNavigate();
    const { isLoading, setIsLoading } = useContext(LoadingContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [shouldSearch, setShouldSearch] = useState(true);
    const [noSearchResult, setNoSearchResult] = useState(false);
    const { showAlert } = useContext(AlertContext);
    const { settings } = useSettings();
    const [paymentType, setPaymentType] = useState('Full');
    const [paymentMode, setPaymentMode] = useState('Online');
    const [paybalAmount, setPaybalAmount] = useState('')
    const formatDateToIST = () => {
        const now = new Date();
        // Convert to milliseconds and adjust for IST (UTC +5:30)
        const offset = 5.5 * 60 * 60 * 1000; // IST offset in milliseconds
        const istTime = new Date(now.getTime() + offset);
        return istTime.toISOString().split('T')[0]; // Format date as YYYY-MM-DD
    };

    const currentDate = formatDateToIST(Date.now());

    const calculateNextMonthDate = () => {
        const now = new Date();
        // now.setMonth(now.getMonth() + 1);
        return now.toISOString().split('T')[0];
    };


    const [booking, setBooking] = useState({
        bookedBy: '',
        seatDetails: [{ seatNumber: '', slot: '', seatValidTill: calculateNextMonthDate(), type: 'new' }],
        bookingDate: currentDate,
        endDate: '',
        transactionNum: '',
        discountCoupon: '',
        discountValue: '',
        totalPrice: '',
        clientTxnId: '',
        amount: '',
        pInfo: '',
        validityInfo: [],
        locker: false,
        securityDeposit: false,
        customerName: '',
        customerEmail: '',
        customerMobile: '',
        redirectUrl: '',
        udf1: '',
        udf2: '',
        udf3: '',
        paymentMode: 'Online',
        pCash: '',
        pOnline: '',
        paymentStatus: 'paid',
        paymentUrl: '',
        upiIdHash: '',
        upiTxnId: '',
        statusRemark: '',
        txnAt: '',
        merchantName: '',
        merchantUpiId: '',
    });

    const fetchStudents = useMemo(() =>
        debounce(async (query) => {
            if (!query) {
                setSearchResults([]);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const response = await fetch(`${host}/bookings/search-students?query=${query}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": localStorage.getItem('token'), // Replace with your actual auth token
                    },
                });
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                if (Array.isArray(data) && data.length === 0) {
                    setNoSearchResult(true);
                }
                setSearchResults(data);
            } catch (error) {
                console.error(error);
            }
            finally {
                setIsLoading(false);
            }
        }, 700), [host, setIsLoading]);

    useEffect(() => {
        if (searchQuery && shouldSearch) {
            setIsLoading(true);
            fetchStudents(searchQuery);
            return () => {
                fetchStudents.cancel();
            };
        } else {
            setSearchResults([]);
            setIsLoading(false);

        }
    }, [fetchStudents, searchQuery, setIsLoading, shouldSearch]);

    const handlePaymentTypeChange = (event) => {
        const newPaymentType = event.target.value;
        setPaymentType(newPaymentType);

        if (newPaymentType === 'Full') {
            setBooking({ ...booking, udf1: '', udf2: '', paymentStatus: 'paid' });
            setPaybalAmount(booking.amount);
        } else if (newPaymentType === 'Partial') {
            setBooking({ ...booking, paymentStatus: 'pending' });
            setPaybalAmount(booking.udf1 || 0);
        }
    };

    const handlePaymentModeChange = (event) => {
        const newPaymentMode = event.target.value;
        setPaymentMode(newPaymentMode);
        if (newPaymentMode === 'Online') {
            setBooking((prevBooking) => ({
                ...prevBooking,
                paymentMode: newPaymentMode,
                pCash: '',
                pOnline: paybalAmount
            }));
        } else if (newPaymentMode === 'Cash') {
            setBooking((prevBooking) => ({
                ...prevBooking,
                paymentMode: newPaymentMode,
                pCash: paybalAmount,
                pOnline: ''
            }));
        } else {
            setBooking((prevBooking) => ({
                ...prevBooking,
                paymentMode: newPaymentMode
            }));
        }
    };

    const handleStudentSelect = (student) => {
        const seatDetails = student.seatAssigned.map(seat => ({
            seatNumber: seat.seatNumber,
            slot: seat.slot,
            seatValidTill: seat.validityDate.split('T')[0], // Converting to yyyy-mm-dd format
            type: 'renewal',
        }));

        setBooking((prevBooking) => ({
            ...prevBooking,
            bookedBy: student.uid,
            customerName: student.name,
            customerEmail: student.email,
            customerMobile: student.phone,
            seatDetails: seatDetails.length > 0 ? seatDetails : [{ seatNumber: '', slot: '', seatValidTill: calculateNextMonthDate(), type: 'new' }],
            pOnline: paybalAmount
        }));
        setSearchQuery(student.uid);  // Set the selected student's name in the search box
        setSearchResults([]);  // Clear the search results
        setShouldSearch(false);
    };

    const handleInputChange = (e, index) => {
        setNoSearchResult(false)
        const { name, value } = e.target;
        if (value === '') {
            setBooking((prevBooking) => ({
                ...prevBooking,
                bookedBy: '',
                customerName: '',
                customerEmail: '',
                customerMobile: '',
                seatDetails: [{ seatNumber: '', slot: '', seatValidTill: calculateNextMonthDate(), type: 'new' }],
            }));
        }
        if (name === 'studentSearch') {
            setShouldSearch(true);
            setSearchQuery(value);
            const query = e.target.value;
            fetchStudents(query);

        } else {
            const list = [...booking.seatDetails];
            list[index][name] = value;
            setBooking({ ...booking, seatDetails: list });
        }
    };

    const handleBookingInputChange = (e) => {
        const { name, value } = e.target;

        // Define fields that should be treated as numbers
        const numericFields = ['udf1', 'pOnline', 'amount', 'udf2', 'pCash'];

        // Convert value to a number if the field is in numericFields, otherwise use the value as is
        const numericValue = numericFields.includes(name) ? parseFloat(value) || 0 : value;

        setBooking((prevBooking) => {
            let newPaybalAmount = paybalAmount;
            let newUdf2 = prevBooking.udf2;
            let newPCash = prevBooking.pCash;
            // Calculate udf2 and pCash based on the input
            if (name === 'udf1') {
                newPaybalAmount = numericValue;
                newUdf2 = prevBooking.amount - numericValue;
            } else if (name === 'pOnline') {
                newPCash = newPaybalAmount - numericValue;
            }
            return {
                ...prevBooking,
                [name]: numericValue,
                udf2: newUdf2,
                pCash: newPCash
            };
        });

        if (name === 'udf1') {
            setPaybalAmount(numericValue);
        }
    };



    const handleRemoveClick = (index) => {
        const list = [...booking.seatDetails];
        list.splice(index, 1);
        setBooking({ ...booking, seatDetails: list });
    };

    const handleAddClick = () => {
        setBooking({
            ...booking,
            seatDetails: [...booking.seatDetails, { seatNumber: '', slot: '', seatValidTill: calculateNextMonthDate(), type: 'new' }],
        });
    };


    const handleDateChange = (e) => {
        setBooking({ ...booking, bookingDate: e.target.value });
    };

    // const handleDiscountChange = (e) => {
    //     const { name, value } = e.target;
    //     setBooking({ ...booking, [name]: value }, calculateTotal);
    // };

    const validateCoupon = async (couponCode, e) => {
        e.preventDefault()
        if (!couponCode || !couponCode.trim() || booking.couponApplied) {
            showAlert("Please enter a coupon code or remove the existing coupon first.", "danger")
            return;
        }
        if (booking.seatDetails.length === 0) {
            showAlert("Select a seat to book in order to apply a coupon", "danger")
            return;
        }

        try {
            const seatSlots = booking.seatDetails.map(seat => seat.slot);
            const response = await fetch(`${host}/coupons/check-coupon`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code: couponCode,
                    productCount: booking.seatDetails.length,
                    productTypes: seatSlots
                })
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message);
            }
            if (response.ok) {
                //console.log('Coupon data received:', data);
                applyDiscount(data);
                setBooking({ ...booking, discountValue: data.discountValue, couponApplied: true })
                //console.log(booking)
            }

            //console.log(data)
            showAlert("Coupon applied successfully!", "success")
        } catch (error) {
            showAlert(`Error applying coupon: ${error.message}`, "danger")
        }
    };

    const applyDiscount = (coupon) => {
        setBooking((prevBooking) => {
            //console.log('Previous booking state:', prevBooking);
            let discountValue = 0;
            const totalPrice = sumSeatLinePrices(prevBooking.seatDetails, settings);

            if (coupon.discountType === 'amount') {
                discountValue = coupon.discountValue;
            } else if (coupon.discountType === 'percentage') {
                discountValue = (totalPrice * coupon.discountValue) / 100;
            }

            //console.log('Calculated discount value:', discountValue);
            const amount = totalPrice - discountValue;

            return {
                ...prevBooking,
                discountCoupon: coupon.code,
                discountValue: coupon.discountValue,
                couponApplied: true,
                totalPrice: totalPrice,
                amount: amount
            };

        });
    };

    const calculateTotal = useCallback(() => {
        setBooking((prevBooking) => {
            const seatsSubtotal = sumSeatLinePrices(prevBooking.seatDetails, settings);
            const lockerCost = prevBooking.locker ? 100 : 0;
            const securityDeposit = prevBooking.securityDeposit ? 100 : 0; // Add 100 if security deposit is selected
            const totalPrice = seatsSubtotal + lockerCost + securityDeposit;
            const discountValue = prevBooking.discountValue || 0;
            const amount = totalPrice - discountValue;
            return {
                ...prevBooking,
                totalPrice,
                amount,
                discountValue,
                couponApplied: prevBooking.couponApplied
            };
        });
    }, [settings]);

    const removeCoupon = () => {
        const totalPrice = booking.totalPrice
        setBooking({ ...booking, discountCoupon: '', discountValue: 0, couponApplied: false, amount: totalPrice });
        showAlert("Coupon removed!!", "success")
    };

    const generateTransactionId = async () => {
        try {
            const txnResponse = await fetch(`${host}/bookings/generate-txn-id`);
            const txnData = await txnResponse.json();

            if (!txnResponse.ok) {
                throw new Error('Failed to generate a unique transaction ID: ' + txnData.message);
            }

            const clientTxnIdAsString = String(txnData.clientTxnId);
            return clientTxnIdAsString;
        } catch (error) {
            console.error('Error during transaction ID generation:', error.message);
            throw error; // Rethrow to handle it in the caller
        }
    };

    const createOrder = async (updatedBooking) => {
        try {
            const response = await fetch(`${host}/bookings/create/direct-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "auth-token": localStorage.getItem('token'),
                },
                body: JSON.stringify(updatedBooking),
            });
            const responseData = await response.json();

            if (!response.ok) {
                throw new Error('Failed to create order: ' + responseData.message);
            }

            //console.log(responseData, 'responsedata');

            await callDirectWebhook(updatedBooking.clientTxnId);

            // Handle webhook data if needed
        } catch (error) {
            console.error('Error during order creation:', error.message);
            showAlert(`Something went wrong: ${error.message}`, 'danger')
        }
    };



    const callDirectWebhook = async (clientTxnId) => {
        try {
            const response = await fetch(`${host}/bookings/api/direct-webhook`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "auth-token": localStorage.getItem('token'),
                },
                body: JSON.stringify({ clientTxnId }),
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error('Failed to call direct webhook: ' + responseData.message);
            }

            //console.log('Direct webhook called successfully');
            showAlert(responseData.message, 'success'); // Show alert with the response message
            setBooking({
                bookedBy: '',
                seatDetails: [{ seatNumber: '', slot: '', seatValidTill: calculateNextMonthDate(), type: 'new' }],
                bookingDate: currentDate,
                endDate: '',
                transactionNum: '',
                discountCoupon: '',
                discountValue: '',
                totalPrice: '',
                clientTxnId: '',
                amount: '',
                pInfo: '',
                validityInfo: [],
                locker: false,
                securityDeposit: false,
                customerName: '',
                customerEmail: '',
                customerMobile: '',
                redirectUrl: '',
                udf1: '',
                udf2: '',
                udf3: '',
                paymentMode: 'Online',
                pCash: '',
                pOnline: '',
                paymentStatus: 'paid',
                paymentUrl: '',
                upiIdHash: '',
                upiTxnId: '',
                statusRemark: '',
                txnAt: '',
                merchantName: '',
                merchantUpiId: '',
            })
            setSearchQuery('')
        } catch (error) {
            console.error('Error calling direct webhook:', error.message);
            showAlert(`Something went wrong: ${error.message}`, 'danger')
        }
    };



    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const clientTxnIdAsString = await generateTransactionId();

            const updatedBooking = {
                ...booking,
                clientTxnId: clientTxnIdAsString,
            };

            // Update the state and proceed to create the order
            setBooking(updatedBooking);
            //console.log(updatedBooking, 'kya hai updated booking me')
            await createOrder(updatedBooking);

        } catch (error) {
            console.error('Error during transaction ID generation:', error.message);
            showAlert(`Something went wrong: ${error.message}`, 'danger')
        }
    };

    function addOneMonth(date) {
        // Get the current day, month, year, and time from the date object
        const currentDay = date.getDate();
        let currentMonth = date.getMonth();
        let currentYear = date.getFullYear();
        const currentHours = date.getHours();
        const currentMinutes = date.getMinutes();
        const currentSeconds = date.getSeconds();
        const currentMilliseconds = date.getMilliseconds();

        // Define the last day of each month
        const lastDayOfMonth = {
            january: 31,
            february: 28, // We'll adjust this for leap years below
            march: 31,
            april: 30,
            may: 31,
            june: 30,
            july: 31,
            august: 31,
            september: 30,
            october: 31,
            november: 30,
            december: 31
        };

        // Check if it's a leap year
        function isLeapYear(year) {
            return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        }

        // Get the last day of the current month
        function getLastDayOfMonth(month, year) {
            const monthNames = Object.keys(lastDayOfMonth);
            if (monthNames[month] === "february" && isLeapYear(year)) {
                return 29; // Adjust February for leap years
            }
            return lastDayOfMonth[monthNames[month]];
        }

        // Check if the current day is the last day of the month
        const lastDayOfCurrentMonth = getLastDayOfMonth(currentMonth, currentYear);
        const isLastDay = currentDay === lastDayOfCurrentMonth;

        //console.log('Before increment:', currentMonth, currentYear);
        // Move to the next month
        currentMonth = (currentMonth + 1) % 12; // Use modulo to wrap around to January
        if (currentMonth === 0) { // If it's January, increment the year
            currentYear += 1;
        }
        //console.log('After increment:', currentMonth, currentYear);
        let newDay;

        if (isLastDay && currentMonth === 1 && currentYear % 4 === 0 && (currentYear % 100 !== 0 || currentYear % 400 === 0)) {
            // It's the last day of February in a leap year
            newDay = 29;
        } else if (isLastDay) {
            // It's the last day of the month, but not a leap year February
            newDay = getLastDayOfMonth(currentMonth, currentYear);
        } else {
            // Not the last day of the month
            newDay = Math.min(currentDay, getLastDayOfMonth(currentMonth, currentYear));
        }

        // Return the new date with the preserved time
        return new Date(currentYear, currentMonth, newDay, currentHours, currentMinutes, currentSeconds, currentMilliseconds);
    }






    const extendValidity = (index) => {
        console.log('extendValidity called for index:', index);
        setBooking((prevBooking) => {
            const newSeatDetails = [...prevBooking.seatDetails];
            let currentDate = new Date(newSeatDetails[index].seatValidTill);

            // console.log('Current Date:', currentDate);
            // console.log('Current Date Month:', currentDate.getMonth());
            // console.log('Current Date Year:', currentDate.getFullYear());

            // If it's the first time extending, save the original date
            // Check if originalSeatValidTill is not set
            if (!newSeatDetails[index].originalSeatValidTill) {
                newSeatDetails[index].originalSeatValidTill = newSeatDetails[index].seatValidTill;
            }

            const newDate = addOneMonth(currentDate);


            // console.log('New Date:', newDate);
            // console.log('New Date Month:', newDate.getMonth());
            // console.log('New Date Year:', newDate.getFullYear());

            // Update the date and track the extension
            newSeatDetails[index].seatValidTill = newDate.toISOString().split('T')[0];

            // Calculate the total extension by comparing the original validity with the current extended date
            const originalValidity = new Date(newSeatDetails[index].originalSeatValidTill);
            const totalExtension = Math.floor((newDate - originalValidity) / (1000 * 60 * 60 * 24)); // Convert milliseconds to days

            // Update the date and track the extension
            newSeatDetails[index].seatValidTill = newDate.toISOString().split('T')[0];
            newSeatDetails[index].totalExtension = totalExtension;

            const newPInfo = [...(prevBooking.validityInfo || [])];
            newPInfo[index] = totalExtension; // Update pInfo for this seat
            const fromToValidity = [...(prevBooking.statusRemark || [])];
            fromToValidity[index] = 'From ' + formatDate(originalValidity) + ' To ' + formatDate(newDate);
            // console.log(fromToValidity, 'fromto')
            return {
                ...prevBooking,
                seatDetails: newSeatDetails,
                validityInfo: newPInfo, // Update the pInfo array
                statusRemark: fromToValidity,
            };
        });
    };


    const handleValidityInputChange = (e, index) => {
        const { value } = e.target;
        const newSeatDetails = [...booking.seatDetails];

        // If the original date is not set, set it now
        if (!newSeatDetails[index].originalSeatValidTill) {
            newSeatDetails[index].originalSeatValidTill = newSeatDetails[index].seatValidTill;
        }

        // Update the specific field
        newSeatDetails[index].seatValidTill = value;

        // Calculate the total extension from the original date
        const originalValidity = new Date(newSeatDetails[index].originalSeatValidTill);
        const currentValidity = new Date(value);
        const totalExtension = Math.floor((currentValidity - originalValidity) / (1000 * 60 * 60 * 24)); // Convert milliseconds to months

        // Update the total extension
        newSeatDetails[index].totalExtension = totalExtension;

        // Update the booking state with the new seat details and save the total extension to pInfo array
        setBooking((prevBooking) => {
            const newPInfo = [...(prevBooking.validityInfo || [])];
            newPInfo[index] = totalExtension; // Update pInfo for this seat

            return {
                ...prevBooking,
                seatDetails: newSeatDetails,
                validityInfo: newPInfo, // Update the pInfo array
            };
        });
    };

    const handleLockerChange = (isLockerSelected) => {
        setBooking((prevBooking) => ({
            ...prevBooking,
            locker: isLockerSelected,
        }));

        // Recalculate total after locker selection change
        calculateTotal();
    };

    const handleSecurityDepositChange = (isSecurityDepositSelected) => {
        setBooking((prevBooking) => ({
            ...prevBooking,
            securityDeposit: isSecurityDepositSelected,
        }));

        // Recalculate total after security deposit selection change
        calculateTotal();
    };


    useEffect(() => {
        calculateTotal();
    }, [booking.seatDetails, booking.discountValue, calculateTotal]);
    useEffect(() => {
        if (paymentType === 'Full') {
            setPaybalAmount(booking.amount);
        } else if (paymentType === 'Partial' && booking.udf1) {
            setPaybalAmount(booking.udf1);
        }
        //console.log(booking, 'where is customer name?')
    }, [booking, paymentType]);

    return (
        <div className="bg-light min-vh-100">
            <div className="container-fluid">
                <div className="row">
                    <Sidedash />
                    <div className="col-md-10 py-4">
                        <div className="container-fluid px-4">
                            {/* Page Header */}
                            <div className="mb-4">
                                <h2 className="fw-bold text-dark mb-1">Create Receipt</h2>
                                <p className="text-muted mb-0">Generate a new booking receipt for customer transactions</p>
                            </div>

                            <form onSubmit={handleSubmit}>
                                {/* Customer Information Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-person-circle me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0"/>
                                                <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1"/>
                                            </svg>
                                            Customer Information
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-lg-6">
                                                <label className="form-label fw-semibold text-dark mb-2">
                                                    Search Existing Customer
                                                    <span className="text-muted fw-normal ms-2" style={{fontSize: '0.875rem'}}>(Optional)</span>
                                                </label>
                                                <div className="position-relative">
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-white border-end-0 mb-3">
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" fill="#6c757d">
                                                                <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
                                                            </svg>
                                                        </span>
                                                        <input 
                                                            name="studentSearch" 
                                                            value={searchQuery} 
                                                            type="search" 
                                                            onChange={(e) => handleInputChange(e)} 
                                                            className="form-control border-start-0 ps-0" 
                                                            placeholder="Search by name, email, phone or ID" 
                                                            style={{fontSize: '0.95rem'}}
                                                        />
                                                        {isLoading && (
                                                            <span className="input-group-text bg-white border-start-0">
                                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {searchResults.length > 0 && (
                                                        <ul className="list-group position-absolute w-100 shadow-lg border-0 mt-1 z-3" style={{maxHeight: '300px', overflowY: 'auto'}}>
                                                            {searchResults.map((student) => (
                                                                <li 
                                                                    key={student._id} 
                                                                    className="list-group-item list-group-item-action border-0 border-bottom py-3 cursor-pointer"
                                                                    onClick={() => handleStudentSelect(student)}
                                                                    style={{cursor: 'pointer'}}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div>
                                                                            <div className="fw-semibold text-dark">{student.name}</div>
                                                                            <div className="text-muted small mt-1">{student.email}</div>
                                                                        </div>
                                                                        <div className="text-end">
                                                                            <div className="badge bg-primary-subtle text-primary">{student.uid}</div>
                                                                            <div className="text-muted small mt-1">{student.phone}</div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {searchQuery && !isLoading && noSearchResult && (
                                                        <div className="alert alert-warning mt-2 mb-0 py-2" role="alert">
                                                            <small>No customer found. Please enter details manually below.</small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <label className="form-label fw-semibold text-dark mb-2">Receipt Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="bookingDate"
                                                    value={booking.bookingDate}
                                                    onChange={handleDateChange}
                                                    style={{fontSize: '0.95rem'}}
                                                />
                                            </div>

                                            <div className="col-12">
                                                <hr className="my-2" />
                                            </div>

                                            <div className="col-lg-4">
                                                <label className="form-label fw-semibold text-dark mb-2">
                                                    Customer Name <span className="text-danger">*</span>
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name='customerName' 
                                                    value={booking.customerName} 
                                                    className="form-control" 
                                                    placeholder="Enter full name" 
                                                    onChange={handleBookingInputChange}
                                                    required
                                                    style={{fontSize: '0.95rem'}}
                                                />
                                            </div>

                                            <div className="col-lg-4">
                                                <label className="form-label fw-semibold text-dark mb-2">
                                                    Mobile Number <span className="text-danger">*</span>
                                                </label>
                                                <input 
                                                    type="tel" 
                                                    name='customerMobile' 
                                                    value={booking.customerMobile} 
                                                    className="form-control" 
                                                    placeholder="Enter 10-digit mobile" 
                                                    onChange={handleBookingInputChange}
                                                    required
                                                    style={{fontSize: '0.95rem'}}
                                                />
                                            </div>

                                            <div className="col-lg-4">
                                                <label className="form-label fw-semibold text-dark mb-2">
                                                    Email Address <span className="text-danger">*</span>
                                                </label>
                                                <input 
                                                    type="email" 
                                                    name='customerEmail' 
                                                    value={booking.customerEmail} 
                                                    className="form-control" 
                                                    placeholder="customer@example.com" 
                                                    onChange={handleBookingInputChange}
                                                    required
                                                    style={{fontSize: '0.95rem'}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Seat Details Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-grid-3x3 me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5zM1.5 1a.5.5 0 0 0-.5.5V5h4V1zM5 6H1v4h4zm1 4h4V6H6zm-1 1H1v3.5a.5.5 0 0 0 .5.5H5zm1 0v4h4v-4zm5 0v4h3.5a.5.5 0 0 0 .5-.5V11zm0-1h4V6h-4zm0-5h4V1.5a.5.5 0 0 0-.5-.5H11zm-1 0V1H6v4z"/>
                                            </svg>
                                            Seat Allocation Details
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        {booking.seatDetails.map((x, i) => (
                                            <div key={i} className="border rounded-3 p-4 mb-3 position-relative bg-light">
                                                {booking.seatDetails.length > 1 && (
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleRemoveClick(i)} 
                                                        className="btn btn-sm btn-light border position-absolute top-0 end-0 m-3"
                                                        style={{width: '32px', height: '32px', padding: '0'}}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill='#dc3545' height="14" width="14">
                                                            <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z" />
                                                        </svg>
                                                    </button>
                                                )}
                                                
                                                <div className="row g-3 mb-2">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Seat Number</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="seatNumber"
                                                            value={x.seatNumber}
                                                            onChange={e => handleInputChange(e, i)}
                                                            placeholder="e.g., A-101"
                                                            style={{fontSize: '0.95rem'}}
                                                        />
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Time Slot</label>
                                                        <select
                                                            className="form-select"
                                                            name="slot"
                                                            value={x.slot}
                                                            onChange={e => handleInputChange(e, i)}
                                                            style={{fontSize: '0.95rem'}}
                                                        >
                                                            <option value="">Select time slot</option>
                                                            <option value="morning">Morning (6 AM - 10 AM)</option>
                                                            <option value="afternoon">Afternoon (10 AM - 2 PM)</option>
                                                            <option value="evening">Evening (2 PM - 6 PM)</option>
                                                            <option value="night">Night (6 PM - 10 PM)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Booking Type</label>
                                                        <select
                                                            className="form-select"
                                                            name="type"
                                                            value={x.type}
                                                            onChange={e => handleInputChange(e, i)}
                                                            style={{fontSize: '0.95rem'}}
                                                        >
                                                            <option value="new">New Booking</option>
                                                            <option value="renewal">Renewal</option>
                                                        </select>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Valid Until</label>
                                                        <div className="d-flex gap-2 align-items-start">
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                name="seatValidTill"
                                                                value={x.seatValidTill}
                                                                onChange={e => handleValidityInputChange(e, i)}
                                                                style={{fontSize: '0.95rem'}}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-success text-nowrap"
                                                                onClick={() => extendValidity(i)}
                                                                style={{fontSize: '0.875rem'}}
                                                            >
                                                                +1 Month
                                                            </button>
                                                        </div>
                                                        {x.totalExtension > 0 && (
                                                            <div className={`mt-2 badge ${x.totalExtension > 61 ? 'bg-danger' : (x.totalExtension > 31 && x.totalExtension <= 61) ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                                Extended by {x.totalExtension} day{x.totalExtension > 1 ? 's' : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {booking.seatDetails.length - 1 === i && (
                                                    <button
                                                        type="button"
                                                        onClick={handleAddClick}
                                                        className="btn btn-outline-primary mt-3"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-circle me-2" viewBox="0 0 16 16">
                                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                                                        </svg>
                                                        Add Another Seat
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Additional Services Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-plus-square me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                                            </svg>
                                            Additional Services
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2 d-block">Locker Required?</label>
                                                <div className="btn-group w-100" role="group">
                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="locker" 
                                                        id="lockerYes" 
                                                        value={true} 
                                                        onChange={() => handleLockerChange(true)} 
                                                        checked={booking.locker === true} 
                                                    />
                                                    <label className="btn btn-outline-primary" htmlFor="lockerYes">Yes</label>

                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="locker" 
                                                        id="lockerNo" 
                                                        value={false} 
                                                        onChange={() => handleLockerChange(false)} 
                                                        checked={booking.locker === false} 
                                                    />
                                                    <label className="btn btn-outline-primary" htmlFor="lockerNo">No</label>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2 d-block">Security Deposit</label>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        name="securityDeposit"
                                                        id="securityDeposit"
                                                        onChange={(e) => handleSecurityDepositChange(e.target.checked)}
                                                        checked={booking.securityDeposit === true}
                                                        style={{width: '3rem', height: '1.5rem'}}
                                                    />
                                                    <label className="form-check-label ms-2" htmlFor="securityDeposit">
                                                        Collect ₹100 security deposit
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Pricing & Discount Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-tag me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M6 4.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m-1 0a.5.5 0 1 0-1 0 .5.5 0 0 0 1 0"/>
                                                <path d="M2 1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 1 6.586V2a1 1 0 0 1 1-1m0 5.586 7 7L13.586 9l-7-7H2z"/>
                                            </svg>
                                            Pricing & Discounts
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2">Coupon Code</label>
                                                <div className="position-relative">
                                                    <input 
                                                        type="text" 
                                                        name='discountCoupon' 
                                                        onChange={(e) => setBooking({ ...booking, discountCoupon: e.target.value })} 
                                                        value={booking.discountCoupon} 
                                                        className="form-control pe-5" 
                                                        placeholder="Enter discount coupon code" 
                                                        disabled={booking.couponApplied}
                                                        style={{fontSize: '0.95rem'}}
                                                    />
                                                    {!booking.couponApplied ? (
                                                        <button 
                                                            type="button"
                                                            className='btn btn-primary btn-sm position-absolute end-0 top-0 pb-2' 
                                                            onClick={(e) => validateCoupon(booking.discountCoupon, e)}
                                                            disabled={!booking.discountCoupon}
                                                        >
                                                            Apply
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            type="button"
                                                            onClick={removeCoupon} 
                                                            className="btn btn-sm btn-outline-danger position-absolute end-0 top-0 m-1"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                {booking.couponApplied && (
                                                    <div className="alert alert-success mt-2 mb-0 py-2" role="alert">
                                                        <small>✓ Coupon applied successfully!</small>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2">Discount Value</label>
                                                <input 
                                                    type="text" 
                                                    name='discountValue' 
                                                    value={booking.discountValue ? `₹${booking.discountValue}` : ''} 
                                                    className="form-control bg-light" 
                                                    placeholder="₹0" 
                                                    disabled 
                                                    style={{fontSize: '0.95rem'}}
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2">Subtotal</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light mb-3" style={{height: 'auto'}}>₹</span>
                                                    <input 
                                                        type="text" 
                                                        name='totalPrice' 
                                                        value={booking.totalPrice} 
                                                        className="form-control bg-light fw-semibold" 
                                                        disabled 
                                                        style={{fontSize: '0.95rem'}}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-dark mb-2">Total Amount</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-success text-white  mb-3" style={{height: 'auto'}}>₹</span>
                                                    <input 
                                                        type="text" 
                                                        name='amount' 
                                                        value={booking.amount} 
                                                        className="form-control bg-light fw-bold text-success" 
                                                        disabled 
                                                        style={{fontSize: '1.1rem'}}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Details Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-credit-card me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z"/>
                                                <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/>
                                            </svg>
                                            Payment Details
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label fw-semibold text-dark mb-2 d-block">Payment Type</label>
                                                <div className="btn-group w-100" role="group" style={{maxWidth: '400px'}}>
                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="paymentType" 
                                                        id="paymentFull" 
                                                        value="Full" 
                                                        checked={paymentType === 'Full'} 
                                                        onChange={handlePaymentTypeChange} 
                                                    />
                                                    <label className="btn btn-outline-success" htmlFor="paymentFull">Full Payment</label>

                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="paymentType" 
                                                        id="paymentPartial" 
                                                        value="Partial" 
                                                        checked={paymentType === 'Partial'} 
                                                        onChange={handlePaymentTypeChange} 
                                                    />
                                                    <label className="btn btn-outline-warning" htmlFor="paymentPartial">Partial Payment</label>
                                                </div>
                                                {paymentType === 'Partial' && (
                                                    <div className="alert alert-info mt-3 mb-0" role="alert">
                                                        <small>
                                                            <strong>Payment Status:</strong> {booking.paymentStatus === 'paid' ? 'Paid' : 'Pending'} | 
                                                            <strong className="ms-2">Amount Payable:</strong> ₹{paybalAmount || booking.amount} | 
                                                            <strong className="ms-2">Amount Due:</strong> ₹{booking.udf2 || '0'}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>

                                            {paymentType === 'Partial' && (
                                                <>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Amount Paying Now</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text mb-3" style={{height: 'auto'}}>₹</span>
                                                            <input 
                                                                type="number" 
                                                                name='udf1' 
                                                                value={booking.udf1} 
                                                                className="form-control" 
                                                                placeholder="Enter amount" 
                                                                onChange={handleBookingInputChange}
                                                                style={{fontSize: '0.95rem'}}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Amount Due</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text bg-light mb-3" style={{height: 'auto'}}>₹</span>
                                                            <input 
                                                                type="text" 
                                                                value={booking.amount - booking.udf1 || 0} 
                                                                className="form-control bg-light fw-semibold" 
                                                                disabled 
                                                                style={{fontSize: '0.95rem'}}
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            <div className="col-12">
                                                <hr className="my-2" />
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label fw-semibold text-dark mb-2 d-block">Payment Mode</label>
                                                <div className="btn-group" role="group">
                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="paymentMode" 
                                                        id="modeCash" 
                                                        value='Cash' 
                                                        onChange={handlePaymentModeChange} 
                                                        checked={paymentMode === 'Cash'} 
                                                    />
                                                    <label className="btn btn-outline-primary" htmlFor="modeCash">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-cash me-2" viewBox="0 0 16 16">
                                                            <path d="M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
                                                            <path d="M0 4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V6a2 2 0 0 1-2-2z"/>
                                                        </svg>
                                                        Cash
                                                    </label>

                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="paymentMode" 
                                                        id="modeOnline" 
                                                        value='Online' 
                                                        onChange={handlePaymentModeChange} 
                                                        checked={paymentMode === 'Online'} 
                                                    />
                                                    <label className="btn btn-outline-primary" htmlFor="modeOnline">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-phone me-2" viewBox="0 0 16 16">
                                                            <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                                                            <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2"/>
                                                        </svg>
                                                        Online
                                                    </label>

                                                    <input 
                                                        type="radio" 
                                                        className="btn-check" 
                                                        name="paymentMode" 
                                                        id="modeMixed" 
                                                        value='Mixed' 
                                                        onChange={handlePaymentModeChange} 
                                                        checked={paymentMode === 'Mixed'} 
                                                    />
                                                    <label className="btn btn-outline-primary" htmlFor="modeMixed">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-shuffle me-2" viewBox="0 0 16 16">
                                                            <path fillRule="evenodd" d="M0 3.5A.5.5 0 0 1 .5 3H1c2.202 0 3.827 1.24 4.874 2.418.49.552.865 1.102 1.126 1.532.26-.43.636-.98 1.126-1.532C9.173 4.24 10.798 3 13 3v1c-1.798 0-3.173 1.01-4.126 2.082A9.6 9.6 0 0 0 7.556 8a9.6 9.6 0 0 0 1.317 1.918C9.828 10.99 11.204 12 13 12v1c-2.202 0-3.827-1.24-4.874-2.418A10.6 10.6 0 0 1 7 9.05c-.26.43-.636.98-1.126 1.532C4.827 11.76 3.202 13 1 13H.5a.5.5 0 0 1 0-1H1c1.798 0 3.173-1.01 4.126-2.082A9.6 9.6 0 0 0 6.444 8a9.6 9.6 0 0 0-1.317-1.918C4.172 5.01 2.796 4 1 4H.5a.5.5 0 0 1-.5-.5"/>
                                                            <path d="M13 5.466V1.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192m0 9v-3.932a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192"/>
                                                        </svg>
                                                        Mixed
                                                    </label>
                                                </div>
                                            </div>

                                            {paymentMode === 'Mixed' && (
                                                <>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Online Amount</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text mb-3" style={{height: 'auto'}}>₹</span>
                                                            <input 
                                                                type="number" 
                                                                name='pOnline' 
                                                                value={booking.pOnline} 
                                                                className="form-control" 
                                                                placeholder="Amount paid online" 
                                                                onChange={handleBookingInputChange}
                                                                style={{fontSize: '0.95rem'}}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold text-dark mb-2">Cash Amount</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text mb-3" style={{height: 'auto'}}>₹</span>
                                                            <input 
                                                                type="number" 
                                                                name='pCash' 
                                                                value={booking.pCash} 
                                                                className="form-control" 
                                                                placeholder="Amount paid in cash" 
                                                                onChange={handleBookingInputChange}
                                                                style={{fontSize: '0.95rem'}}
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Notes Section */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h5 className="mb-0 fw-semibold text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-journal-text me-2 mb-1" viewBox="0 0 16 16">
                                                <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5"/>
                                                <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2"/>
                                                <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z"/>
                                            </svg>
                                            Additional Notes
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <label className="form-label fw-semibold text-dark mb-2">Internal Notes (Optional)</label>
                                        <textarea 
                                            name='udf3' 
                                            value={booking.udf3} 
                                            className="form-control" 
                                            placeholder="Add any special instructions, remarks, or notes for this booking..." 
                                            onChange={handleBookingInputChange} 
                                            rows='4'
                                            style={{fontSize: '0.95rem'}}
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="d-flex gap-3 justify-content-end">
                                    <button 
                                        type="button" 
                                        className="btn btn-lg btn-outline-secondary px-5"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        disabled={!searchQuery && !booking.customerName && !booking.customerEmail} 
                                        type="submit" 
                                        className="btn btn-lg btn-success px-5 shadow-sm"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-check-circle me-2 mb-1" viewBox="0 0 16 16">
                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                            <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
                                        </svg>
                                        Create Receipt
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BookingForm